import apiService from '../../utils/apiService';
import {
  renameResponseIndicatesBlockedItems,
  stripHtmlToPlainMessage,
} from '../../utils/renameMessageUtils';
import {
  DELETED_ITEMS_API,
  DISALLOWED_FILE_NAME_CHAR_REGEX,
  DISALLOWED_FILE_NAME_CHAR_MESSAGE,
} from '../../../Constants';

export const MAX_FILENAME_LENGTH = 256;
export const FILENAME_TOO_LONG_MESSAGE =
  'The name exceeds 256 characters. File or folder cannot be renamed.';

export const isFilenameTooLong = (name) =>
  typeof name === 'string' && name.length > MAX_FILENAME_LENGTH;

export const getFileNameAndExtension = (filename, { isFile: isFileHint } = {}) => {
  const safeName = filename || '';

  if (isFileHint === false) {
    return { name: safeName, extension: '', isFile: false };
  }

  const lastDotIndex = safeName.lastIndexOf('.');
  const hasExtension = lastDotIndex > 0;

  if (!hasExtension) {
    return { name: safeName, extension: '', isFile: isFileHint === true };
  }

  return {
    name: safeName.substring(0, lastDotIndex),
    extension: safeName.substring(lastDotIndex),
    isFile: isFileHint !== false,
  };
};

export const fetchSoftDeletedNames = async (currentPath) => {
  const res = await apiService.post(DELETED_ITEMS_API, { current_path: currentPath });
  const body = res?.data;
  if (body?.status_code === 200 && Array.isArray(body.data)) {
    const names = body.data.map((row) => row.originalName).filter(Boolean);
    return [...new Set(names)];
  }
  return [];
};

export const getRenameInputFieldError = (raw, cleaned, finalName) => {
  if (raw !== cleaned) {
    return DISALLOWED_FILE_NAME_CHAR_MESSAGE;
  }
  if (isFilenameTooLong(finalName)) {
    return FILENAME_TOO_LONG_MESSAGE;
  }
  return '';
};

export const applyRenameValidationResult = (
  validation,
  { setError, setEditableName, originalName }
) => {
  if (validation?.type === 'alert') {
    alert(validation.message);
    return false;
  }
  if (validation?.type === 'error') {
    setError(validation.message);
    return false;
  }
  if (validation?.type === 'empty') {
    alert('Folder name cannot be empty');
    setEditableName(originalName);
    return false;
  }
  return true;
};

export const getRenameApiErrorMessage = (data) => {
  const failureMessage = getRenameFailureMessage(data);
  if (failureMessage) {
    return stripHtmlToPlainMessage(failureMessage) || 'Error renaming files.';
  }
  if (renameResponseIndicatesBlockedItems(data?.message)) {
    return stripHtmlToPlainMessage(data.message) || 'Rename could not be completed.';
  }
  return null;
};

export const validateRenameFolderInput = ({
  folderName,
  renamefolder,
  existingNames,
  softDeletedNames,
}) => {
  if (renamefolder === folderName) {
    return { type: 'alert', message: 'You have not made any changes' };
  }

  if (DISALLOWED_FILE_NAME_CHAR_REGEX.test(folderName)) {
    return { type: 'error', message: DISALLOWED_FILE_NAME_CHAR_MESSAGE };
  }

  if (existingNames.includes(folderName)) {
    return {
      type: 'error',
      message: `A file or folder with the name "${folderName}" already exists in this location.`,
    };
  }

  if (softDeletedNames.includes(folderName)) {
    return {
      type: 'error',
      message: `A file or folder with the name "${folderName}" already exists in soft deleted item list.`,
    };
  }

  if (isFilenameTooLong(folderName)) {
    return {
      type: 'error',
      message: FILENAME_TOO_LONG_MESSAGE,
    };
  }

  if (folderName === undefined || folderName === '') {
    return { type: 'empty' };
  }

  return null;
};

export const buildRenamePayload = (currentPath, renamefolder, folderName) => ({
  data: {
    current_path: currentPath,
    rename_items: [
      {
        rename_filename: renamefolder,
        rename_filename_new: folderName,
      },
    ],
  },
});

export const getRenameFailureMessage = (data) => {
  if (!data) {
    return 'Error renaming files.';
  }

  if (data.status_code && data.status_code !== 200) {
    return data.message || 'Error renaming files.';
  }

  return '';
};
